import { Icon } from '@iconify/react';
import { MouseEvent, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { TopicDto } from '@/Domain/api/Conversation/dto/Topic.dto';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { TopicForm } from '@/Domain/hooks/AI/useTopicForm/useTopicForm.hook';

type Props = {
  form: UseFormReturn<TopicForm>;
  index: number;
  topic: TopicDto;
  minTopics: number;
  isEditMode: boolean;
  topicLength: number;
  isUpdatePending: boolean;
  setIsEditMode: (mode: boolean) => void;
  onDelete: (topicId: number) => void;
  onReset: () => void;
};

export const TopicFormItem = ({
  index,
  topic,
  form,
  minTopics,
  topicLength,
  onDelete,
  onReset,
  isUpdatePending,
  isEditMode,
  setIsEditMode,
}: Props) => {
  const { t } = useTranslation('insights_page');

  const handleEditState = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    setIsEditMode(true);
  };

  const handleDelete = () => onDelete(topic.id);

  const handleReset = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    onReset();
    setIsEditMode(false);
  };

  const isLastTopic = useMemo(() => minTopics >= topicLength, [topicLength]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  return (
    <>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <section className="grid grid-cols-[0.5fr_2fr_4fr_0.5fr] gap-x-2">
          <p className="mt-2 text-foreground-subtle text-sm whitespace-nowrap">
            {t('manage_topics.form.topic', { index: index + 1 })}
          </p>

          <FormField
            control={form.control}
            name="topic"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  {isEditMode ? (
                    <Input
                      autoFocus
                      placeholder={t('manage_topics.form.topic_placeholder')}
                      size="sm"
                      {...field}
                    />
                  ) : (
                    <p className="text-sm px-4 py-2 border border-transparent">{topic.topic}</p>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="topic_description"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  {isEditMode ? (
                    <AutosizeTextarea
                      maxLength={255}
                      autoComplete="off"
                      className="rounded-xl !min-h-[34px] !px-4 pb-2 pt-[9px] leading-none break-words"
                      placeholder={t('manage_topics.form.description_placeholder')}
                      minHeight={12}
                      {...field}
                    />
                  ) : (
                    <p
                      className="text-sm px-4 py-2 border border-transparent whitespace-pre-line"
                      style={{ wordBreak: 'break-word' }}
                    >
                      {topic.topic_description}
                    </p>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {isEditMode ? (
            <section className="flex gap-2">
              <Button variant="secondary" size="icon" type="submit" disabled={isUpdatePending}>
                <Icon icon="mingcute:check-line" />
              </Button>
              <Button variant="danger" size="icon" disabled={!topic.topic} onClick={handleReset}>
                <Icon icon="mingcute:close-line" />
              </Button>
            </section>
          ) : (
            <section className="flex gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" size="icon" onClick={handleEditState}>
                    <Icon icon="mingcute:pencil-line" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent align="end" alignOffset={-135}>
                  <Trans i18nKey="insights_page:tooltip.edit_topic" />
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild type="button">
                  <AlertDialogTrigger asChild type="button" disabled={isLastTopic}>
                    <Button
                      variant="secondary"
                      size="icon"
                      disabled={isLastTopic}
                      className="hover:!text-red-500"
                    >
                      <Icon icon="mingcute:delete-line" />
                    </Button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent align="end">
                  {isLastTopic ? t('tooltip.no_delete_last_topic') : t('tooltip.delete_topic')}
                </TooltipContent>
              </Tooltip>
            </section>
          )}
        </section>

        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Icon
                icon="mingcute:alert-fill"
                className="bg-red-100 rounded-lg text-red-500 p-2 box-content"
              />
              {t('dialog.delete_topic.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('dialog.delete_topic.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <Separator />
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Button size="sm" variant="secondary">
                {' '}
                {t('dialog.delete_topic.button.cancel')}
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              {t('dialog.delete_topic.button.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
